/**
 * useYDoc - React hook for Y.Doc lifecycle management
 *
 * Manages a Yjs document with:
 * - Local IndexedDB persistence for offline support
 * - Cleanup on unmount
 * - Stable reference (doesn't change between renders)
 *
 * @param boardId - Unique identifier for the board
 * @returns { ydoc, synced } - The Y.Doc and local sync status
 */

import { useState, useEffect, useRef } from 'react';
import * as Y from 'yjs';
import { IndexeddbPersistence } from 'y-indexeddb';

interface UseYDocReturn {
  // The Yjs document (stable reference)
  ydoc: Y.Doc;
  // Whether IndexedDB has synced (local cache loaded)
  localSynced: boolean;
}

export function useYDoc(boardId: string): UseYDocReturn {
  // Create Y.Doc once per hook instance (stable reference)
  const ydocRef = useRef<Y.Doc | null>(null);
  if (!ydocRef.current) {
    ydocRef.current = new Y.Doc();
  }

  const [localSynced, setLocalSynced] = useState(false);

  useEffect(() => {
    const ydoc = ydocRef.current!;

    // Create IndexedDB persistence
    // This loads cached doc from browser storage (fast initial load)
    const persistence = new IndexeddbPersistence(`board-${boardId}`, ydoc);

    persistence.on('synced', () => {
      console.log('[useYDoc] IndexedDB synced');
      setLocalSynced(true);
    });

    // Cleanup on unmount or boardId change
    return () => {
      console.log('[useYDoc] Cleaning up IndexedDB persistence');
      persistence.destroy();
      setLocalSynced(false);
    };
  }, [boardId]);

  return {
    ydoc: ydocRef.current!,
    localSynced,
  };
}
